import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Shield, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [step, setStep] = useState(1); // 1: phone, 2: profile
  const [phoneNumber, setPhoneNumber] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');

  const handleLogin = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        phone_number: formattedPhone
      });
      
      setPhoneNumber(formattedPhone);
      setToken(response.data.token);

      if (response.data.requires_profile) {
        setStep(2);
        toast.success('Welcome! Please complete your profile (or skip to continue).');
      } else {
        // Fetch latest user data
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${response.data.token}` }
        });
        
        login(response.data.token, userResponse.data);
        toast.success('Login successful!');
        
        // Redirect based on credits
        if (userResponse.data.analyses_remaining > 0) {
          navigate('/analyze');
        } else {
          navigate('/payment');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteProfile = async () => {
    if (!name || !email) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      // Update profile
      await axios.put(`${API}/auth/profile`, {
        name: name,
        email: email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Fetch latest user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      login(token, userResponse.data);
      toast.success('Profile completed successfully!');
      
      // Redirect based on credits
      if (userResponse.data.analyses_remaining > 0) {
        navigate('/analyze');
      } else {
        navigate('/payment');
      }
    } catch (error) {
      console.error('Complete profile error:', error);
      toast.error('Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  const skipProfile = async () => {
    setLoading(true);
    try {
      // Fetch user data without profile
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      login(token, userResponse.data);
      toast.success('Welcome to Resume Rx!');
      
      // Redirect based on credits
      if (userResponse.data.analyses_remaining > 0) {
        navigate('/analyze');
      } else {
        navigate('/payment');
      }
    } catch (error) {
      console.error('Skip profile error:', error);
      toast.error('Failed to continue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome to Resume Rx
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {step === 1 && 'Enter your phone number to continue'}
              {step === 2 && 'Complete your profile (optional - you can skip)'}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1: Phone Number */}
            {step === 1 && (
              <motion.div
                key="phone"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      data-testid="phone-input"
                      type="tel"
                      placeholder="+91 9876543210"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="pl-10"
                      onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    />
                  </div>
                </div>

                <Button
                  data-testid="login-button"
                  onClick={handleLogin}
                  disabled={loading}
                  className="w-full bg-emerald-500 text-white hover:bg-emerald-600 rounded-full py-6 text-lg font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Logging in...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </motion.div>
            )}

            {/* Step 2: Profile Completion */}
            {step === 2 && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Full Name
                  </label>
                  <Input
                    data-testid="name-input"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Email Address
                  </label>
                  <Input
                    data-testid="email-input"
                    type="email"
                    placeholder="john@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <Button
                  data-testid="complete-profile-button"
                  onClick={handleCompleteProfile}
                  disabled={loading || !name || !email}
                  className="w-full bg-emerald-500 text-white hover:bg-emerald-600 rounded-full py-6 text-lg font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    'Complete Profile'
                  )}
                </Button>

                <Button
                  data-testid="skip-profile-button"
                  onClick={skipProfile}
                  disabled={loading}
                  variant="ghost"
                  className="w-full"
                >
                  Skip for Now
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="text-center text-sm text-slate-600 dark:text-slate-400 mt-6">
          By continuing, you agree to our Terms & Privacy Policy
        </p>
      </motion.div>
    </div>
  );
}
